import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smile, Paperclip, AtSign, Send, X, Loader2, Image, Video, FileText, File, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const FILE_ICONS = {
  image: Image,
  video: Video,
  document: FileText,
  audio: FileText,
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function EnhancedChatInput({
  onSend,
  placeholder = "Type a message...",
  disabled = false,
  loading = false,
  showAIWriter = false,
  onAIWrite = null,
  className = "",
  buttonColor = "from-cyan-500 to-teal-500",
  buttonTextColor = "text-white",
  maxHeight = "120px",
}) {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [aiWriterLoading, setAiWriterLoading] = useState(false);
  
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const searchUsersForMention = useCallback(async (query) => {
    if (query.length < 2) {
      setMentionUsers([]);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMentionUsers(await res.json());
      }
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  }, [token]);

  useEffect(() => {
    if (mentionSearch) {
      const timer = setTimeout(() => searchUsersForMention(mentionSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [mentionSearch, searchUsersForMention]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    
    // Check for @ mention trigger
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      const spaceIndex = afterAt.indexOf(' ');
      if (spaceIndex === -1 && afterAt.length > 0) {
        setMentionSearch(afterAt);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = message.lastIndexOf('@');
    const beforeAt = message.substring(0, lastAtIndex);
    setMessage(`${beforeAt}@${user.name} `);
    setSelectedMentions([...selectedMentions, user.id]);
    setShowMentions(false);
    setMentionSearch('');
    inputRef.current?.focus();
  };

  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const uploadedFiles = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${API_URL}/api/social/chat/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedFiles.push(data);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setAttachments([...attachments, ...uploadedFiles]);
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if ((!message.trim() && attachments.length === 0) || disabled || loading) return;
    
    onSend({
      content: message.trim(),
      attachments: attachments.length > 0 ? attachments : null,
      mentions: selectedMentions.length > 0 ? selectedMentions : null
    });
    
    setMessage('');
    setAttachments([]);
    setSelectedMentions([]);
  };

  const handleAIWriter = async () => {
    if (!onAIWrite || !message.trim()) return;
    
    setAiWriterLoading(true);
    try {
      const enhancedMessage = await onAIWrite(message);
      if (enhancedMessage) {
        setMessage(enhancedMessage);
      }
    } catch (error) {
      toast.error('AI writing failed');
    } finally {
      setAiWriterLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Emoji Picker */}
      <AnimatePresence>
        {showEmojiPicker && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-0 mb-2 z-50"
          >
            <Picker
              data={data}
              onEmojiSelect={handleEmojiSelect}
              theme="dark"
              previewPosition="none"
              skinTonePosition="none"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mention Suggestions */}
      <AnimatePresence>
        {showMentions && mentionUsers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-0 mb-2 w-64 bg-[#1a2235] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
          >
            <div className="p-2 border-b border-white/10">
              <p className="text-xs text-gray-400">Mention someone</p>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {mentionUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => insertMention(user)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors text-left"
                >
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{user.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
          {attachments.map((file, index) => {
            const Icon = FILE_ICONS[file.type] || File;
            return (
              <div key={index} className="relative flex-shrink-0">
                {file.type === 'image' ? (
                  <img src={file.url} alt={file.name} className="h-16 w-16 object-cover rounded-lg border border-white/10" />
                ) : (
                  <div className="h-16 w-16 bg-white/10 rounded-lg flex flex-col items-center justify-center border border-white/10">
                    <Icon className="w-5 h-5 text-gray-400" />
                    <span className="text-[10px] text-gray-500 mt-1 px-1 truncate max-w-full">{file.name?.split('.').pop()}</span>
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Input Area */}
      <div className="flex flex-col gap-2">
        {/* Text Input Row */}
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || loading}
            className="flex-1 input-dark min-h-[60px] resize-none py-3"
            style={{ maxHeight }}
            data-testid="enhanced-chat-input"
          />

          {/* Send Button */}
          <Button
            type="button"
            onClick={handleSend}
            disabled={(!message.trim() && attachments.length === 0) || disabled || loading}
            className={`bg-gradient-to-r ${buttonColor} ${buttonTextColor} px-4 h-[60px]`}
            data-testid="enhanced-send-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>

        {/* Action Buttons Row - Below input */}
        <div className="flex items-center gap-1">
          {/* Emoji Button */}
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-amber-400 flex items-center gap-1"
            title="Add emoji"
            data-testid="emoji-btn"
          >
            <Smile className="w-5 h-5" />
            <span className="text-xs hidden sm:inline">Emoji</span>
          </button>

          {/* Attachment Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-cyan-400 flex items-center gap-1"
            title="Attach file"
            data-testid="attach-btn"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Paperclip className="w-5 h-5" />
            )}
            <span className="text-xs hidden sm:inline">Attach</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Mention Button */}
          <button
            type="button"
            onClick={() => {
              setMessage(prev => prev + '@');
              inputRef.current?.focus();
            }}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-purple-400 flex items-center gap-1"
            title="Mention someone"
            data-testid="mention-btn"
          >
            <AtSign className="w-5 h-5" />
            <span className="text-xs hidden sm:inline">Mention</span>
          </button>

          {/* AI Writer Button (optional) */}
          {showAIWriter && onAIWrite && (
            <button
              type="button"
              onClick={handleAIWriter}
              disabled={aiWriterLoading || !message.trim()}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-amber-400 disabled:opacity-50 flex items-center gap-1"
              title="AI Writer - Enhance your message"
              data-testid="ai-writer-btn"
            >
              {aiWriterLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              <span className="text-xs hidden sm:inline">AI Writer</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
